import { useEffect, useMemo, useState } from 'react';
import {
  CopyPlus,
  Home,
  ListPlus,
  RefreshCcw,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import TenantSiteImageUploadField from '../../components/admin/TenantSiteImageUploadField';
import { defaultHomePageContent, mergeHomePageContent } from '../../content/defaultTenantSitePages';
import { useAuth } from '../../context/AuthContext';
import { tenantSiteImageAssetConfigs } from '../../services/TenantSiteImageStorageService';
import { TenantSitePagesService } from '../../services/TenantSitePagesService';
import type {
  TenantHomePageContent,
  TenantSiteAsset,
  TenantSiteFeatureItem,
} from '../../types/site-pages';

const iconOptions = [
  { value: 'heart', label: 'Heart' },
  { value: 'activity', label: 'Activity' },
  { value: 'shield', label: 'Shield' },
  { value: 'users', label: 'Users' },
  { value: 'award', label: 'Award' },
  { value: 'book-open', label: 'Book Open' },
] as const;

const createEmptyAsset = (): TenantSiteAsset => ({
  provider: 'firebase-storage',
  storagePath: '',
  downloadUrl: '',
  alt: '',
});

const createFeatureItem = (): TenantSiteFeatureItem => ({
  id: `feature-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  iconKey: 'heart',
  title: '',
  description: '',
});

const cloneHomeContent = (content: TenantHomePageContent): TenantHomePageContent =>
  JSON.parse(JSON.stringify(content)) as TenantHomePageContent;

const HomePageEditor = () => {
  const { user, selectedClient } = useAuth();
  const clientId = selectedClient?.clientId ?? '';
  const [formData, setFormData] = useState<TenantHomePageContent>(() =>
    cloneHomeContent(defaultHomePageContent),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedOverride, setHasSavedOverride] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const featureItems = useMemo(() => formData.features?.items ?? [], [formData.features?.items]);

  const loadContent = async () => {
    if (!clientId) {
      setFormData(cloneHomeContent(defaultHomePageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const pageRecord = await TenantSitePagesService.getByClientId(clientId, 'home');
      const merged = mergeHomePageContent(pageRecord?.contentJson ?? null);

      setFormData(cloneHomeContent(merged));
      setHasSavedOverride(Boolean(pageRecord));
      setLastUpdatedAt(pageRecord?.updatedAt ?? null);
    } catch (error) {
      console.error('Erro ao carregar conteúdo da home:', error);
      toast.error('Não foi possível carregar o conteúdo da página inicial.');
      setFormData(cloneHomeContent(defaultHomePageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadContent();
  }, [clientId]);

  const updateSection = <
    TSection extends keyof TenantHomePageContent,
    TField extends keyof NonNullable<TenantHomePageContent[TSection]>,
  >(
    section: TSection,
    field: TField,
    value: NonNullable<TenantHomePageContent[TSection]>[TField],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] ?? {}),
        [field]: value,
      },
    }));
  };

  const updateHeroAsset = (
    field: 'desktopImage' | 'mobileImage',
    updater: (asset: TenantSiteAsset) => TenantSiteAsset,
  ) => {
    updateSection('hero', field, updater((formData.hero?.[field] as TenantSiteAsset) ?? createEmptyAsset()));
  };

  const updateFeatureItem = (
    featureId: string,
    updater: (item: TenantSiteFeatureItem) => TenantSiteFeatureItem,
  ) => {
    const nextItems = [...featureItems].map((item) =>
      item.id === featureId ? updater(item) : item,
    );
    updateSection('features', 'items', nextItems);
  };

  const addFeatureItem = () => {
    updateSection('features', 'items', [...featureItems, createFeatureItem()]);
  };

  const removeFeatureItem = (featureId: string) => {
    updateSection(
      'features',
      'items',
      featureItems.filter((item) => item.id !== featureId),
    );
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clientId || !user?.userId) {
      toast.error('Selecione um tenant antes de salvar.');
      return;
    }

    setIsSaving(true);

    try {
      const saved = await TenantSitePagesService.upsertForClient(
        clientId,
        'home',
        formData,
        user.userId,
      );

      setFormData(cloneHomeContent(mergeHomePageContent(saved.contentJson)));
      setHasSavedOverride(true);
      setLastUpdatedAt(saved.updatedAt);
      toast.success('Página inicial atualizada com sucesso.');
    } catch (error) {
      console.error('Erro ao salvar página inicial:', error);
      toast.error('Não foi possível salvar a página inicial.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setFormData(cloneHomeContent(defaultHomePageContent));
  };

  const handleDeleteOverride = async () => {
    if (!clientId) {
      toast.error('Selecione um tenant antes de remover o override.');
      return;
    }

    try {
      setIsSaving(true);
      await TenantSitePagesService.deleteForClient(clientId, 'home');
      toast.success('As personalizações foram removidas e a versão padrão da página inicial foi restaurada.');
      await loadContent();
    } catch (error) {
      console.error('Erro ao remover override da home:', error);
      toast.error('Não foi possível remover o override da home.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedClient) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Selecione um tenant para editar a página inicial.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
              <Home className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Página Inicial</h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Edite os blocos tenant da home: hero, diferenciais, depoimentos, notícias e CTA.
                As imagens do hero são enviadas diretamente para o Firebase Storage.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-800">Tenant:</span> {selectedClient.clientName}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Origem atual:</span>{' '}
              {hasSavedOverride ? 'Conteúdo salvo no banco' : 'Fallback do sistema'}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Última atualização:</span>{' '}
              {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString('pt-BR') : 'Não salvo'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Hero</h3>
                <p className="text-sm text-slate-500">Texto principal, CTAs e imagens desktop/mobile.</p>
              </div>
              <button
                type="button"
                onClick={() => void loadContent()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <RefreshCcw className="h-4 w-4" />
                Recarregar
              </button>
            </div>

            <div className="grid gap-4">
              <input
                type="text"
                value={formData.hero?.title ?? ''}
                onChange={(event) => updateSection('hero', 'title', event.target.value)}
                placeholder="Título principal"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <textarea
                rows={4}
                value={formData.hero?.subtitle ?? ''}
                onChange={(event) => updateSection('hero', 'subtitle', event.target.value)}
                placeholder="Subtítulo"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />

              <div className="grid gap-4 md:grid-cols-2">
                <input
                  type="text"
                  value={formData.hero?.primaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateSection('hero', 'primaryCta', {
                      ...(formData.hero?.primaryCta ?? { path: '/agendar', variant: 'primary' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Texto do botão principal"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.hero?.primaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateSection('hero', 'primaryCta', {
                      ...(formData.hero?.primaryCta ?? { label: 'Agendar Consulta', variant: 'primary' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/agendar"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.hero?.secondaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateSection('hero', 'secondaryCta', {
                      ...(formData.hero?.secondaryCta ?? { path: '/servicos', variant: 'secondary' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Texto do botão secundário"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.hero?.secondaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateSection('hero', 'secondaryCta', {
                      ...(formData.hero?.secondaryCta ?? { label: 'Nossos Serviços', variant: 'secondary' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/servicos"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {([
                  ['desktopImage', 'Imagem desktop'],
                  ['mobileImage', 'Imagem mobile'],
                ] as const).map(([field, label]) => (
                  <div key={field} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <TenantSiteImageUploadField
                      asset={formData.hero?.[field] ?? null}
                      assetConfig={
                        field === 'desktopImage'
                          ? tenantSiteImageAssetConfigs.homeHeroDesktop
                          : tenantSiteImageAssetConfigs.homeHeroMobile
                      }
                      assetPath={field === 'desktopImage' ? 'hero/desktop' : 'hero/mobile'}
                      clientId={clientId}
                      label={label}
                      pageKey="home"
                      onUploaded={(asset) =>
                        updateHeroAsset(field, (current) => ({
                          ...current,
                          ...asset,
                          alt: current.alt || asset.alt || '',
                        }))
                      }
                      onClear={() => updateHeroAsset(field, () => createEmptyAsset())}
                    />

                    <input
                      type="text"
                      value={formData.hero?.[field]?.alt ?? ''}
                      onChange={(event) =>
                        updateHeroAsset(field, (asset) => ({ ...asset, alt: event.target.value }))
                      }
                      placeholder={`Texto alternativo da ${label.toLowerCase()}`}
                      className="mt-4 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Diferenciais da home</h3>
                <p className="text-sm text-slate-500">Bloco de cards exibido logo após o hero.</p>
              </div>
              <button
                type="button"
                onClick={addFeatureItem}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <ListPlus className="h-4 w-4" />
                Card
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={formData.features?.title ?? ''}
                onChange={(event) => updateSection('features', 'title', event.target.value)}
                placeholder="Título da seção"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <textarea
                rows={3}
                value={formData.features?.subtitle ?? ''}
                onChange={(event) => updateSection('features', 'subtitle', event.target.value)}
                placeholder="Subtítulo da seção"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />

              <div className="space-y-4">
                {featureItems.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-800">{item.title || 'Novo card'}</p>
                      <button
                        type="button"
                        onClick={() => removeFeatureItem(item.id)}
                        className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <select
                        value={item.iconKey ?? 'heart'}
                        onChange={(event) =>
                          updateFeatureItem(item.id, (current) => ({
                            ...current,
                            iconKey: event.target.value,
                          }))
                        }
                        className="rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        {iconOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(event) =>
                          updateFeatureItem(item.id, (current) => ({
                            ...current,
                            title: event.target.value,
                          }))
                        }
                        placeholder="Título"
                        className="md:col-span-2 rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                      <textarea
                        rows={3}
                        value={item.description}
                        onChange={(event) =>
                          updateFeatureItem(item.id, (current) => ({
                            ...current,
                            description: event.target.value,
                          }))
                        }
                        placeholder="Descrição"
                        className="md:col-span-3 rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Títulos de seções</h3>
            <div className="mt-5 grid gap-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="mb-3 font-medium text-slate-800">Depoimentos</p>
                <div className="grid gap-3">
                  <input
                    type="text"
                    value={formData.testimonials?.title ?? ''}
                    onChange={(event) => updateSection('testimonials', 'title', event.target.value)}
                    placeholder="Título"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <textarea
                    rows={3}
                    value={formData.testimonials?.subtitle ?? ''}
                    onChange={(event) => updateSection('testimonials', 'subtitle', event.target.value)}
                    placeholder="Subtítulo"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="mb-3 font-medium text-slate-800">Notícias e artigos</p>
                <div className="grid gap-3">
                  <input
                    type="text"
                    value={formData.newsPreview?.title ?? ''}
                    onChange={(event) => updateSection('newsPreview', 'title', event.target.value)}
                    placeholder="Título"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <textarea
                    rows={3}
                    value={formData.newsPreview?.subtitle ?? ''}
                    onChange={(event) => updateSection('newsPreview', 'subtitle', event.target.value)}
                    placeholder="Subtítulo"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <input
                    type="text"
                    value={formData.newsPreview?.viewAllLabel ?? ''}
                    onChange={(event) => updateSection('newsPreview', 'viewAllLabel', event.target.value)}
                    placeholder="Texto do link ver tudo"
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">CTA final</h3>
            <div className="mt-5 grid gap-4">
              <input
                type="text"
                value={formData.cta?.title ?? ''}
                onChange={(event) => updateSection('cta', 'title', event.target.value)}
                placeholder="Título"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <textarea
                rows={3}
                value={formData.cta?.body ?? ''}
                onChange={(event) => updateSection('cta', 'body', event.target.value)}
                placeholder="Texto"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { path: '/agendar', variant: 'primary' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Botão principal"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { label: 'Agendar Consulta', variant: 'primary' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/agendar"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.cta?.secondaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'secondaryCta', {
                      ...(formData.cta?.secondaryCta ?? { path: '/contato', variant: 'secondary' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Botão secundário"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="text"
                  value={formData.cta?.secondaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'secondaryCta', {
                      ...(formData.cta?.secondaryCta ?? { label: 'Fale Conosco', variant: 'secondary' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/contato"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleResetToDefault}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCcw className="h-4 w-4" />
              Carregar padrão
            </button>

            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar página inicial'}
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Resumo rápido</h3>
            <div className="mt-5 space-y-3 text-sm text-slate-600">
              <p>
                <span className="font-semibold text-slate-800">Hero:</span> {formData.hero?.title || 'Não preenchido'}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Cards de diferenciais:</span> {featureItems.length}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Título de depoimentos:</span>{' '}
                {formData.testimonials?.title || 'Não preenchido'}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Título de notícias:</span>{' '}
                {formData.newsPreview?.title || 'Não preenchido'}
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 shadow-sm">
            <p className="font-semibold">Escopo desta etapa</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Hero tenant com imagem desktop e mobile personalizáveis.</li>
              <li>Cards de diferenciais editáveis por tenant.</li>
              <li>Títulos de depoimentos e notícias configuráveis.</li>
              <li>CTA final configurável por tenant.</li>
            </ul>
          </div>

          {hasSavedOverride && (
            <button
              type="button"
              onClick={() => void handleDeleteOverride()}
              disabled={isSaving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              Remover override salvo
            </button>
          )}

          {isLoading && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              Carregando conteúdo da página inicial...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePageEditor;
